import { NavLink, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useAuth } from "../../stores/auth";
import { desktopNavItems } from "../../config/storefront-nav";
import { Icon } from "../icons";

/**
 * Desktop-only rail. Persistent brand block, animated active pill, and a
 * standing call-to-action — the desktop counterpart to the mobile bottom nav.
 */
export function DesktopSidebar() {
  const { user, moduleEnabled, config } = useAuth();
  const items = desktopNavItems(moduleEnabled, !!user, config);

  return (
    <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-white/5 bg-surface/40 backdrop-blur-xl lg:flex">
      <Link to="/" className="flex items-center justify-center border-b border-white/5 px-5 py-5">
        <img src="/logo-wordmark.png" alt="Mr Gaole" className="h-8 w-auto max-w-[140px] object-contain" />
      </Link>

      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
        {items.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === "/"} className="relative">
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="sidebar-active"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    className="absolute inset-0 rounded-xl bg-gradient-brand shadow-lg shadow-brand-pink/25"
                  />
                )}
                <span
                  className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                    isActive ? "font-semibold text-white" : "text-white/55 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon icon={item.icon} size="lg" className="shrink-0 opacity-90" />
                  <span className="truncate">{item.label}</span>
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {moduleEnabled("packs") && (
        <div className="p-3">
          <Link
            to="/packs"
            className="group relative block overflow-hidden rounded-2xl neon-ring bg-gradient-to-br from-brand-pink/25 via-surface-2 to-brand-blue/25 p-4"
          >
            <img
              src="/art/card-back.svg"
              alt=""
              className="absolute -right-6 -top-3 h-24 w-auto rotate-12 opacity-25 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110"
            />
            <p className="relative font-display text-xs font-bold">Feeling lucky?</p>
            <p className="relative mt-1 max-w-28 text-[10px] leading-snug text-white/55">
              Pick a pack, tear the foil, flip your pulls.
            </p>
            <span className="relative mt-3 inline-flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-[10px] font-bold transition-colors group-hover:bg-white/20">
              <Sparkles size={11} /> Open a pack
            </span>
          </Link>
        </div>
      )}
    </aside>
  );
}
